import { ChevronDown, ScrollText } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DateRangePicker, lastNDays } from '@/shared/components/date-range-picker';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { ExportActions } from '@/shared/components/export-actions';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { formatDateTime } from '@/shared/lib/format';
import { DEFAULT_TIME_ZONE, NON_MARKET_TIME_NOTE } from '@/shared/lib/time-zone';
import { cn } from '@/shared/lib/utils';

import { exportAuditLog } from '../api/audit-log.api';
import { useAuditLog } from '../api/audit-log.queries';
import { AuditChange } from '../components/audit-change';
import { ENTITY_TYPES, actionLabel, entityTypeLabel } from '../lib/audit-labels';

const PAGE_SIZE = 25;
const ANY = 'all';

/**
 * Every change made through the Admin: who, when, what, the reason, and the
 * recorded before and after. Filter by type (and id, from a link), period.
 */
export function AuditLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fallback = useMemo(() => lastNDays(30, DEFAULT_TIME_ZONE), []);
  const range = {
    from: searchParams.get('from') ?? fallback.from,
    to: searchParams.get('to') ?? fallback.to,
  };
  const entityType = searchParams.get('type');
  const entityId = searchParams.get('id');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<number | null>(null);

  const filters = {
    entityType: entityType ?? undefined,
    entityId: entityType && entityId ? entityId : undefined,
    from: range.from,
    to: range.to,
  };
  const log = useAuditLog({ page, pageSize: PAGE_SIZE, ...filters });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setPage(1);
    setSearchParams(next, { replace: true });
  };

  const items = log.data?.items ?? [];
  const totalPages = log.data?.totalPages ?? 1;

  return (
    <PageContainer>
      <PageHeader
        title="Audit log"
        description={`Every change made through the Admin, with who made it, when, and why ${NON_MARKET_TIME_NOTE}`}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <ExportActions
              excel={() => exportAuditLog(filters)}
              pdf={{
                list: 'audit-log',
                rows: items.length,
                filters: { from: range.from, to: range.to, type: entityType ?? 'any' },
              }}
            />
            <span data-print="hide">
              <DateRangePicker
                value={range}
                timeZone={DEFAULT_TIME_ZONE}
                onChange={(value) => update({ from: value.from, to: value.to })}
              />
            </span>
          </span>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-print="hide">
        <Select
          value={entityType ?? ANY}
          onValueChange={(value) => update({ type: value === ANY ? null : value, id: null })}
        >
          <SelectTrigger size="sm" className="h-8 w-48 text-xs" aria-label="Filter by type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY} className="text-xs">
              Every type
            </SelectItem>
            {Object.entries(ENTITY_TYPES).map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {entityType && entityId && (
          <Button variant="outline" size="sm" onClick={() => update({ id: null })}>
            {entityTypeLabel(entityType)} #{entityId} ×
          </Button>
        )}
      </div>

      {log.isLoading ? (
        <Skeleton className="h-64" />
      ) : log.error ? (
        <ErrorState error={log.error} onRetry={() => void log.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No changes recorded"
          description="Nothing was changed through the Admin for these filters in this period."
        />
      ) : (
        <div className="bg-card overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>
                  <span className="sr-only">Details</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((entry) => {
                const expanded = open === entry.id;
                return (
                  <Fragment key={entry.id}>
                    <TableRow>
                      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                        {formatDateTime(entry.occurredAtUtc, DEFAULT_TIME_ZONE)}
                      </TableCell>
                      <TableCell>{entry.actorUserName ?? `#${entry.actorUserId ?? '?'}`}</TableCell>
                      <TableCell>
                        <span className="font-medium">{actionLabel(entry.action)}</span>{' '}
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => update({ type: entry.entityType, id: entry.entityId })}
                        >
                          {entityTypeLabel(entry.entityType)} #{entry.entityId}
                        </button>
                      </TableCell>
                      <TableCell className="max-w-64 truncate" title={entry.reason ?? undefined}>
                        {entry.reason ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Hide' : 'Show'} change ${entry.id}`}
                          onClick={() => setOpen(expanded ? null : entry.id)}
                        >
                          <ChevronDown
                            className={cn('size-4 transition-transform', expanded && 'rotate-180')}
                            aria-hidden
                          />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={5}>
                          <AuditChange entry={entry} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <span className="text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </PageContainer>
  );
}
